import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import { formatCents, formatTs } from '@/lib/referral-utils'
import { Card, CardHeader } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Badge } from '@/components/ui/Badge'
import { PiiField } from '@/components/ui/PiiField'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { usePresentation } from '@/components/layout/AppShell'

interface ReferrerBalance {
  id: string
  name: string | null
  email: string
  payout_method: string | null
  paypal_email: string | null
  balance_cents: number
  reward_count: number
  oldest_reward_at: number
}

interface PayoutResult {
  payout_id: string
  amount_cents: number
  reward_count: number
  method: string
  external_transfer_id?: string
}

export function PayoutsPage() {
  const queryClient = useQueryClient()
  const { presenting } = usePresentation()
  const [payingUserId, setPayingUserId] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<{ userId: string; result: PayoutResult } | null>(null)
  const [error, setError] = useState<{ userId: string; message: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referral-balances'],
    queryFn: () => authApi.get<{ balances: ReferrerBalance[] }>('/api/v1/admin/referral/balances'),
  })

  async function handlePayout(user: ReferrerBalance, method: string) {
    setPayingUserId(user.id)
    setError(null)
    setLastResult(null)
    try {
      const res = await authApi.post<PayoutResult>('/api/v1/admin/referral/payouts', {
        user_id: user.id,
        method,
      })
      setLastResult({ userId: user.id, result: res })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-balances'] })
    } catch (e) {
      setError({ userId: user.id, message: String(e) })
    } finally {
      setPayingUserId(null)
    }
  }

  const totalBalance = (data?.balances ?? []).reduce((sum, b) => sum + b.balance_cents, 0)

  const columns: Column<ReferrerBalance>[] = [
    {
      id: 'name',
      header: 'Referrer',
      cell: (row) => (
        <div>
          <PiiField value={row.name || 'Unknown'} presenting={presenting} className="font-medium" />
          <PiiField value={row.email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
        </div>
      ),
    },
    {
      id: 'balance',
      header: 'Balance',
      cell: (row) => (
        <span className="tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCents(row.balance_cents)}
        </span>
      ),
      className: 'text-right',
    },
    {
      id: 'rewards',
      header: 'Rewards',
      cell: (row) => <span className="tabular-nums">{row.reward_count}</span>,
      className: 'text-right',
    },
    {
      id: 'oldest',
      header: 'Oldest',
      cell: (row) => (
        <span className="text-xs text-[var(--content-secondary)] tabular-nums">
          {formatTs(row.oldest_reward_at)}
        </span>
      ),
    },
    {
      id: 'method',
      header: 'Payout Method',
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          {row.paypal_email ? (
            <Badge variant="brand">PayPal</Badge>
          ) : row.payout_method === 'stripe_credit' ? (
            <Badge variant="default">Stripe Credit</Badge>
          ) : (
            <Badge variant="warning">Not set</Badge>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => {
        const isPaying = payingUserId === row.id
        const hasPaypal = !!row.paypal_email

        if (lastResult?.userId === row.id) {
          return (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              Paid {formatCents(lastResult.result.amount_cents)}
            </span>
          )
        }

        if (error?.userId === row.id) {
          return <span className="text-xs text-red-500">{error.message}</span>
        }

        return (
          <div className="flex items-center gap-2 justify-end">
            {hasPaypal && (
              <button
                onClick={(e) => { e.stopPropagation(); handlePayout(row, 'paypal') }}
                disabled={isPaying}
                className="px-3 py-1 text-xs rounded-full bg-[#0070ba] text-white hover:bg-[#005ea6] disabled:opacity-50 transition-colors cursor-pointer"
              >
                {isPaying ? '...' : 'PayPal'}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handlePayout(row, 'stripe_credit') }}
              disabled={isPaying}
              className="px-3 py-1 text-xs rounded-full bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content-secondary)] hover:bg-[var(--border-subtle)] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {isPaying && !row.paypal_email ? '...' : 'Credit'}
            </button>
          </div>
        )
      },
      className: 'text-right min-w-[140px]',
    },
  ]

  if (isLoading) {
    return <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Payouts</h1>
        {totalBalance > 0 && (
          <div className="text-sm text-[var(--content-secondary)]">
            Total outstanding: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCents(totalBalance)}</span>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <h3 className="font-heading font-semibold text-sm">Referrers with Unpaid Balance</h3>
          <p className="text-xs text-[var(--content-tertiary)] mt-0.5">
            Shows referrers with credited cash rewards ready for payout. Click PayPal or Credit to pay.
          </p>
        </CardHeader>
        <DataTable
          columns={columns}
          data={data?.balances ?? []}
          keyExtractor={(row) => row.id}
          emptyMessage="No unpaid balances"
        />
      </Card>
    </div>
  )
}
